/**
 * Sample data for the corporate online-visit design preview. Organization and doctors are
 * fictional on purpose: no real doctor should appear to be part of a plan that does not exist yet.
 */
import fooladLogo from './assets/foolad-logo.png';
import type { CorporateDoctor, CorporateOrganization, CorporatePlan } from './types';

/**
 * Logo: the mark from the header logo of the company's own site (msc.ir), cropped and sharpened,
 * bundled with the app (216×96). The real integration takes the organization's vector file.
 */
export const SAMPLE_ORGANIZATION: CorporateOrganization = {
  name: 'فولاد مبارکه',
  fullName: 'گروه فولاد مبارکه',
  planTitle: 'طرح سلامت کارکنان',
  logo: { src: fooladLogo.src, width: 216, height: 96, box: { x: 0, width: 216 } },
};

/**
 * The plan of the one rolled-out account (see rollout.ts) until a backend serves it: 5 free online
 * visits a year, 3 left. Nothing is deducted yet — the factor refuses to pay with it (see factor).
 */
export const LIVE_PLAN: CorporatePlan = {
  kind: 'quota',
  organization: SAMPLE_ORGANIZATION,
  total: 5,
  remaining: 3,
  renewsOn: '۱ فروردین',
  familyAllowed: true,
};

/** The signed-in employee (fictional). */
export const SAMPLE_MEMBER = { firstName: 'نگار', fullName: 'نگار محمودی', cell: '0912***4567' };

export type CorporateScenario = 'budget' | 'quota' | 'cap' | 'exhausted' | 'none';

export const CORPORATE_SCENARIOS: { key: CorporateScenario; label: string }[] = [
  { key: 'budget', label: 'بودجهٔ سازمان (همه رایگان)' },
  { key: 'quota', label: 'سهمیهٔ تعدادی' },
  { key: 'cap', label: 'سقف ریالی' },
  { key: 'exhausted', label: 'سهمیهٔ تمام‌شده' },
  { key: 'none', label: 'کاربر عادی' },
];

export const planForScenario = (scenario: CorporateScenario): CorporatePlan | null => {
  switch (scenario) {
    case 'budget':
      return { kind: 'budget', organization: SAMPLE_ORGANIZATION, familyAllowed: true };
    case 'quota':
      return { kind: 'quota', organization: SAMPLE_ORGANIZATION, total: 5, remaining: 3, renewsOn: '۱ آذر', familyAllowed: true };
    case 'exhausted':
      return { kind: 'quota', organization: SAMPLE_ORGANIZATION, total: 5, remaining: 0, renewsOn: '۱ آذر', familyAllowed: true };
    case 'cap':
      return { kind: 'cap', organization: SAMPLE_ORGANIZATION, capToman: 300_000, familyAllowed: true };
    default:
      return null;
  }
};

/** A «پوست» search in Tehran: most doctors offer both online visit and in-person booking. */
export const SAMPLE_DOCTORS: CorporateDoctor[] = [
  {
    id: 'salehi',
    name: 'مریم صالحی',
    expertise: 'متخصص پوست، مو و زیبایی',
    priceToman: 320_000,
    inNetwork: true,
    hasInPerson: true,
    rate: 4.8,
    rateCount: 412,
    satisfaction: 96,
    address: 'تهران، خیابان ولیعصر، بالاتر از میدان ونک',
    tags: ['جوش و آکنه', 'ریزش مو', 'لک صورت'],
  },
  {
    id: 'rezaei',
    name: 'علی رضایی',
    expertise: 'فوق تخصص پوست و مو',
    priceToman: 450_000,
    inNetwork: true,
    hasInPerson: true,
    rate: 4.9,
    rateCount: 1090,
    satisfaction: 98,
    address: 'تهران، میدان هفت تیر، ساختمان پزشکان',
    tags: ['اگزما', 'پسوریازیس', 'ریزش مو'],
  },
  {
    id: 'ahmadi',
    name: 'سارا احمدی',
    expertise: 'متخصص پوست و مو',
    priceToman: 280_000,
    inNetwork: false,
    hasInPerson: true,
    rate: 4.7,
    rateCount: 265,
    satisfaction: 94,
    address: 'تهران، خیابان شریعتی، بالاتر از پل رومی',
    tags: ['زگیل', 'جوش'],
  },
  {
    id: 'nouri',
    name: 'حمید نوری',
    expertise: 'دکترای پزشکی عمومی، پوست',
    priceToman: 180_000,
    inNetwork: true,
    hasInPerson: false,
    rate: 4.6,
    rateCount: 158,
    satisfaction: 92,
    address: 'تهران، خیابان آزادی',
    tags: ['خارش پوست', 'آکنه'],
  },
];
